/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {resolvePermission} from '@ion/core/src/entities/EntityUtils';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {
  EntityName,
  EntityOperation
} from '@ion/core/src/types/entities';

/**
 * The `useHasPermission` hook provides a component specific interface onto the
 * underlying application specific `Features` API, which is always to serve as the
 * single source of authoritative truth for all `Role`, `Feature`, and `Permission`
 * access.
 *
 * While this hook can be used directly, applications are encouraged to provide
 * a specific `useHasPermission` implementations which simply wraps this hook and
 * passes the application's `Features` implementation argument as a convenience
 * (rather than having components use this hook directly and need to explicitly
 * import and provide a reference to the `Features` API).
 *
 *
 * ```typescript
 *  // ion/packages/<some-application>/src/security/useHasPermission.ts
 *  import {
 *   useHasPermission as useHasRolePermission
 *  } from '@ion/applications/src/security/useHasPermission';
 *  import {
 *    EntityName,
 *    EntityOperation
 *  } from '@ion/core/src/types/entities';
 *  import Features from '@/security/Features';
 *
 *  // application specific implementation simply delegates to the useHasPermission
 *  // hook from '@ion/applications/src/security/useHasPermission', passing in the
 *  // provided arguments while also providing a reference to the application specific
 *  // Features instance ...
 *  export const useHasPermission = (
 *    entity: EntityName, operation: EntityOperation): boolean => (
 *    useHasRolePermission(entity, operation, Features)
 *  )
 * ```
 *
 * @param   entity An `Entity` enum value or the string literal entity name.
 * @param   operation An `Operation` enum value or the string literal operation.
 * @param   features A reference to the application specific `ApplicationFeatures`
 *          implementation.
 * @returns `true` if the users applications roles includes the given permission,
 *          otherwise, `false`.
 */
export const useHasPermission = (
  entity: EntityName, operation: EntityOperation, features: IApplicationFeatures): boolean => (
  features.hasPermission(entity, operation)
);
